import React, { useState, useCallback, useEffect } from 'react';
import {
  FileText,
  Upload,
  CheckCircle,
  AlertTriangle,
  Download,
  Loader2,
  X,
  FileCheck,
  Sparkles,
  Shield,
  Info
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { isValidPagesFile, getReadableFileSize } from '../utils/documentHelpers';
import { useLocaleContext } from '../context/LocaleContext';
import { getTranslation } from '../utils/translations';
import { getLocalizedSlugSync } from '../blog/slugMapping';
import { getKnowledgeArticleUrlSync } from '../knowledge/slugMapping';
import { useSubscription } from '../hooks/useSubscription';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import type { User } from '@supabase/supabase-js';

interface PagesCleanerWidgetProps {
  user: User | null;
  isGridLayout?: boolean;
}

const PagesCleanerWidget: React.FC<PagesCleanerWidgetProps> = ({ user, isGridLayout = false }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  // CLS FIX: Removed animateIn state - was dead code (defined but never used in JSX)
  const [processingStartTime, setProcessingStartTime] = useState<number>(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [LoginModal, setLoginModal] = useState<React.ComponentType<any> | null>(null);

  const { locale } = useLocaleContext();
  const {
    hasActiveSubscription,
    getSubscriptionType,
    loading: subscriptionLoading,
  } = useSubscription();

  const hasPremium = hasActiveSubscription();
  const subscriptionType = getSubscriptionType();

  // CLS FIX: Removed animation setTimeout - was dead code (animateIn never used)

  // Dynamically load login modal only when needed
  useEffect(() => {
    if (showLoginModal && !LoginModal) {
      import('./DocumentCleanerLoginModal').then((module) => {
        setLoginModal(() => module.default);
      });
    }
  }, [showLoginModal, LoginModal]);

  // Cleanup worker on unmount
  useEffect(() => {
    return () => {
      // Dynamically import and terminate worker on cleanup
      import('../utils/docxCleanerWorker').then((module) => {
        module.terminateDocumentWorker();
      });
    };
  }, []);

  const handleFileSelect = useCallback((file: File) => {
    setError(null);
    setSuccess(false);
    setShowNotice(false);

    // Validate file
    if (!isValidPagesFile(file)) {
      const invalidFileMessages: Record<string, string> = {
        en: 'Invalid file type. Please upload a .pages file.',
        de: 'Ungültiger Dateityp. Bitte laden Sie eine .pages-Datei hoch.',
        'pt-BR': 'Tipo de arquivo inválido. Por favor, envie um arquivo .pages.',
        'ms-MY': 'Jenis fail tidak sah. Sila muat naik fail .pages.',
        es: 'Tipo de archivo no válido. Por favor, sube un archivo .pages.',
        fr: 'Type de fichier invalide. Veuillez télécharger un fichier .pages.',
        ja: '無効なファイル形式です。.pagesファイルをアップロードしてください。',
        id: 'Jenis file tidak valid. Silakan unggah file .pages.',
      };
      setError(invalidFileMessages[locale] || invalidFileMessages.en);
      return;
    }

    // Check file size (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      const fileTooLargeMessages: Record<string, string> = {
        en: 'File too large. Maximum size is 50MB.',
        de: 'Datei zu groß. Maximale Größe ist 50MB.',
        'pt-BR': 'Arquivo muito grande. Tamanho máximo é 50MB.',
        'ms-MY': 'Fail terlalu besar. Saiz maksimum ialah 50MB.',
        es: 'Archivo demasiado grande. El tamaño máximo es 50MB.',
        fr: 'Fichier trop volumineux. La taille maximale est de 50 Mo.',
        ja: 'ファイルが大きすぎます。最大サイズは50MBです。',
        id: 'File terlalu besar. Ukuran maksimum adalah 50MB.',
      };
      setError(fileTooLargeMessages[locale] || fileTooLargeMessages.en);
      return;
    }

    setSelectedFile(file);
  }, [locale, hasPremium, subscriptionType]);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleCleanDocument = async () => {
    if (!selectedFile) return;

    // Check if user is signed in
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    // Check if user has premium subscription
    if (!hasPremium) {
      setShowPremiumModal(true);
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(false);
    const processingMessages: Record<string, string> = {
      en: 'Processing document...',
      de: 'Dokument wird verarbeitet...',
      'pt-BR': 'Processando documento...',
      'ms-MY': 'Memproses dokumen...',
      es: 'Procesando documento...',
      fr: 'Traitement du document...',
      ja: 'ドキュメントを処理中...',
      id: 'Memproses dokumen...',
    };
    setProgressMessage(processingMessages[locale] || processingMessages.en);
    const startTime = Date.now();
    setProcessingStartTime(startTime);

    try {
      // Dynamically import worker functions only when needed
      const { cleanPagesWorker } = await import('../utils/docxCleanerWorker');

      // Use Web Worker for non-blocking document processing
      await cleanPagesWorker(selectedFile, (message) => {
        setProgressMessage(message);
      });

      const processingTime = Date.now() - startTime;

      setSuccess(true);
      setShowNotice(true);
      const successMessages: Record<string, string> = {
        en: 'Success! Your cleaned document has been downloaded.',
        de: 'Erfolgreich! Ihr bereinigtes Dokument wurde heruntergeladen.',
        'pt-BR': 'Sucesso! Seu documento limpo foi baixado.',
        'ms-MY': 'Berjaya! Dokumen anda yang telah dibersihkan telah dimuat turun.',
        es: '¡Éxito! Tu documento limpio ha sido descargado.',
        fr: 'Succès ! Votre document nettoyé a été téléchargé.',
        ja: '成功！クリーンなドキュメントがダウンロードされました。',
        id: 'Berhasil! Dokumen bersih Anda telah diunduh.',
      };
      setProgressMessage(successMessages[locale] || successMessages.en);

      // Clear file and success after a delay, but keep notice visible
      setTimeout(() => {
        setSelectedFile(null);
        setSuccess(false);
        setProgressMessage('');
        // Reset file input so re-selecting the same file triggers onChange
        const fileInput = document.getElementById('pages-file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }, 5000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';

      setError(errorMessage);
      setProgressMessage('');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setError(null);
    setSuccess(false);
    setProgressMessage('');
  };

  if (subscriptionLoading && user) {
    return (
      <div className="mt-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
      </div>
    );
  }

  return (
    <>
      <PremiumUpgradeModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature="document_cleaning"
      />

      <div
        id="pages-cleaner"
        className={`${!isGridLayout ? 'mt-12 md:mt-16' : 'flex-1'}`}
      >
        {/* Main Card */}
        <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-soft border border-neutral-200 dark:border-neutral-800 overflow-hidden h-full flex flex-col">
          {/* Header Section */}
          <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 border-b border-neutral-100 dark:border-neutral-800">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-accent-50 dark:bg-accent-900/20 rounded-xl border border-accent-100 dark:border-accent-800">
                  <FileText className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                    {getTranslation(locale, 'pagesCleaner.title')}
                  </h2>
                  <p className="text-neutral-500 dark:text-neutral-400 text-sm">
                    {getTranslation(locale, 'pagesCleaner.subtitle')}
                  </p>
                </div>
              </div>

              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg text-xs font-medium border border-green-200 dark:border-green-800">
                <Shield size={12} />
                {{
                  en: '100% private',
                  de: '100% privat',
                  'pt-BR': '100% privado',
                  'ms-MY': '100% peribadi',
                  es: '100% privado',
                  fr: '100% privé',
                  ja: '100%プライベート',
                  id: '100% pribadi',
                }[locale] || '100% private'}
              </div>
            </div>
          </div>

          {/* Upload Area */}
          <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 flex-1">
            <div
              className={`relative w-full min-h-[180px] rounded-xl transition-all duration-200 cursor-pointer ${
                isDragging
                  ? 'bg-accent-50 dark:bg-accent-900/20 border-2 border-accent-400 dark:border-accent-600'
                  : 'bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 hover:border-accent-300 dark:hover:border-accent-600'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => document.getElementById('pages-file-input')?.click()}
            >
              <input
                id="pages-file-input"
                type="file"
                accept=".pages"
                onChange={handleFileInputChange}
                className="hidden"
              />

              <div className="flex flex-col items-center justify-center p-6 text-center h-full min-h-[180px]">
                {selectedFile ? (
                  <div className="w-full">
                    <div className="flex items-center justify-between bg-white dark:bg-neutral-800 rounded-lg p-4 border border-neutral-200 dark:border-neutral-700">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 bg-accent-50 dark:bg-accent-900/20 rounded-lg border border-accent-100 dark:border-accent-800 shrink-0">
                          <FileCheck className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-neutral-900 dark:text-white text-sm truncate">
                            {selectedFile.name}
                          </p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">
                            {getReadableFileSize(selectedFile.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearFile();
                        }}
                        className="p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors shrink-0"
                        aria-label="Remove file"
                      >
                        <X size={16} className="text-neutral-500 dark:text-neutral-400" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="p-3 bg-accent-50 dark:bg-accent-900/20 rounded-xl border border-accent-100 dark:border-accent-800 mb-4">
                      <Upload className="w-8 h-8 text-accent-600 dark:text-accent-400" />
                    </div>
                    <h3 className="text-sm font-medium text-neutral-900 dark:text-white mb-1">
                      {getTranslation(locale, 'pagesCleaner.uploadTitle')}
                    </h3>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
                      {getTranslation(locale, 'pagesCleaner.uploadDescription')}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-neutral-600 dark:text-neutral-400">
                      <span className="flex items-center gap-1">
                        <FileText size={12} />
                        .pages
                      </span>
                      <span>•</span>
                      <span>Max 50MB</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Status Messages */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    {{
                      en: 'Error',
                      de: 'Fehler',
                      'pt-BR': 'Erro',
                      'ms-MY': 'Ralat',
                      es: 'Error',
                      fr: 'Erreur',
                      ja: 'エラー',
                      id: 'Kesalahan',
                    }[locale] || 'Error'}
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">{error}</p>
                </div>
              </div>
            )}

            {success && (
              <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3 animate-scale-in">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    {{
                      en: 'Success!',
                      de: 'Erfolgreich!',
                      'pt-BR': 'Sucesso!',
                      'ms-MY': 'Berjaya!',
                      es: '¡Éxito!',
                      fr: 'Succès !',
                      ja: '成功！',
                      id: 'Berhasil!',
                    }[locale] || 'Success!'}
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-300 mt-0.5">{progressMessage}</p>
                </div>
              </div>
            )}

            {/* FAQ: Still seeing watermarks - shown after successful cleaning */}
            {showNotice && (
              <div className="mt-4 p-5 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-200 dark:border-amber-800/30">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-base text-neutral-900 dark:text-white mb-1.5">
                      {getTranslation(locale, "watermarkRemover.educationalNotice.stillSeeing")}
                    </p>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                      {getTranslation(locale, "watermarkRemover.educationalNotice.stillSeeingAnswer")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Educational Notice - shown after successful cleaning */}
            {showNotice && (
              <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-200 dark:border-blue-800/30">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-neutral-700 dark:text-neutral-300">
                    <p className="font-medium text-neutral-900 dark:text-white mb-2">
                      {getTranslation(locale, "watermarkRemover.educationalNotice.title")}
                    </p>
                    <ul className="space-y-1.5 text-neutral-600 dark:text-neutral-400 list-disc list-inside">
                      <li>{getTranslation(locale, "watermarkRemover.educationalNotice.removed")}</li>
                      <li>{getTranslation(locale, "watermarkRemover.educationalNotice.formatting")}</li>
                    </ul>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <Link
                        to={`${locale === 'en' ? '' : locale === 'pt-BR' ? '/pt-br' : locale === 'ms-MY' ? '/my' : `/${locale}`}/blog/${getLocalizedSlugSync('why-ai-detector-saying-writing-is-ai', locale)}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors underline underline-offset-2"
                      >
                        {getTranslation(locale, "watermarkRemover.educationalNotice.learnMore")}
                      </Link>
                      <Link
                        to={getKnowledgeArticleUrlSync('watermark-removal-vs-ai-detection', locale)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors underline underline-offset-2"
                      >
                        {getTranslation(locale, "watermarkRemover.educationalNotice.removalVsDetection")}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isProcessing && progressMessage && (
              <div className="mt-4 p-3 bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-800 rounded-lg flex items-center gap-3">
                <div className="w-4 h-4 border-2 border-accent-200 border-t-accent-600 dark:border-accent-700 dark:border-t-accent-400 rounded-full animate-spin shrink-0" />
                <p className="text-sm font-medium text-accent-800 dark:text-accent-200">{progressMessage}</p>
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 bg-neutral-50 dark:bg-neutral-800/30 border-t border-neutral-100 dark:border-neutral-800 mt-auto">
            <button
              onClick={handleCleanDocument}
              disabled={!selectedFile || isProcessing}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-accent-600 hover:bg-accent-700 disabled:bg-neutral-300 dark:disabled:bg-neutral-700 text-white disabled:text-neutral-500 dark:disabled:text-neutral-400 rounded-xl font-medium transition-colors disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{{
                    en: 'Processing...',
                    de: 'Verarbeite...',
                    'pt-BR': 'Processando...',
                    'ms-MY': 'Memproses...',
                    es: 'Procesando...',
                    fr: 'Traitement...',
                    ja: '処理中...',
                    id: 'Memproses...',
                  }[locale] || 'Processing...'}</span>
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  <span>{getTranslation(locale, 'pagesCleaner.cleanButton')}</span>
                  <Download size={18} />
                </>
              )}
            </button>

            <p className="text-center text-xs text-neutral-500 dark:text-neutral-400 mt-3">
              {getTranslation(locale, 'pagesCleaner.privacyNote')}
            </p>
          </div>
        </div>

        {/* Feature Highlights - Only show when not in grid layout */}
        {!isGridLayout && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white dark:bg-neutral-900 rounded-xl p-4 border border-neutral-200 dark:border-neutral-800">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-accent-50 dark:bg-accent-900/20 rounded-lg border border-accent-100 dark:border-accent-800 shrink-0">
                  <Shield size={16} className="text-accent-600 dark:text-accent-400" />
                </div>
                <div>
                  <h3 className="font-medium text-neutral-900 dark:text-white text-sm">
                    {{
                      en: 'Fully Private',
                      de: 'Vollständig privat',
                      'pt-BR': 'Totalmente privado',
                      'ms-MY': 'Sepenuhnya peribadi',
                      es: 'Totalmente privado',
                      fr: 'Totalement privé',
                      ja: '完全にプライベート',
                      id: 'Sepenuhnya pribadi',
                    }[locale] || 'Fully Private'}
                  </h3>
                  <p className="text-neutral-500 dark:text-neutral-400 text-xs mt-0.5">
                    {{
                      en: 'Processed in your browser',
                      de: 'Verarbeitung im Browser',
                      'pt-BR': 'Processado no seu navegador',
                      'ms-MY': 'Diproses dalam pelayar anda',
                      es: 'Procesado en tu navegador',
                      fr: 'Traité dans votre navigateur',
                      ja: 'ブラウザ内で処理',
                      id: 'Diproses di browser Anda',
                    }[locale] || 'Processed in your browser'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-neutral-900 rounded-xl p-4 border border-neutral-200 dark:border-neutral-800">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-accent-50 dark:bg-accent-900/20 rounded-lg border border-accent-100 dark:border-accent-800 shrink-0">
                  <FileCheck size={16} className="text-accent-600 dark:text-accent-400" />
                </div>
                <div>
                  <h3 className="font-medium text-neutral-900 dark:text-white text-sm">
                    {{
                      en: 'Keeps Formatting',
                      de: 'Formatierung erhalten',
                      'pt-BR': 'Mantém formatação',
                      'ms-MY': 'Kekalkan pemformatan',
                      es: 'Conserva el formato',
                      fr: 'Conserve le formatage',
                      ja: '書式を保持',
                      id: 'Mempertahankan format',
                    }[locale] || 'Keeps Formatting'}
                  </h3>
                  <p className="text-neutral-500 dark:text-neutral-400 text-xs mt-0.5">
                    {{
                      en: 'Document stays identical',
                      de: 'Dokument bleibt identisch',
                      'pt-BR': 'Documento permanece idêntico',
                      'ms-MY': 'Dokumen kekal sama',
                      es: 'El documento permanece idéntico',
                      fr: 'Le document reste identique',
                      ja: 'ドキュメントはそのまま',
                      id: 'Dokumen tetap sama',
                    }[locale] || 'Document stays identical'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-neutral-900 rounded-xl p-4 border border-neutral-200 dark:border-neutral-800">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 shrink-0">
                  <Sparkles size={16} className="text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-medium text-neutral-900 dark:text-white text-sm">
                    {{
                      en: 'Instant Processing',
                      de: 'Sofort bereit',
                      'pt-BR': 'Processamento instantâneo',
                      'ms-MY': 'Pemprosesan segera',
                      es: 'Procesamiento instantáneo',
                      fr: 'Traitement instantané',
                      ja: '即時処理',
                      id: 'Pemrosesan instan',
                    }[locale] || 'Instant Processing'}
                  </h3>
                  <p className="text-neutral-500 dark:text-neutral-400 text-xs mt-0.5">
                    {{
                      en: 'Cleaned in seconds',
                      de: 'Bereinigt in Sekunden',
                      'pt-BR': 'Limpo em segundos',
                      'ms-MY': 'Dibersihkan dalam beberapa saat',
                      es: 'Limpiado en segundos',
                      fr: 'Nettoyé en quelques secondes',
                      ja: '数秒でクリーン',
                      id: 'Dibersihkan dalam hitungan detik',
                    }[locale] || 'Cleaned in seconds'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Login Modal */}
        {LoginModal && (
          <LoginModal
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            documentType="pages"
          />
        )}
      </div>
    </>
  );
};

export default PagesCleanerWidget;
